import { useState, useEffect } from 'preact/hooks'

export const UniversalSizeFilter = ({ values, onFilter }) => {
  const [activeSizeType, setActiveSizeType] = useState('')

  const parsedSizes = parseSizeData(values)
  const { universalSizes, nonUniversalSizes } = parsedSizes

  const sizeTypeOrder = ['US', 'UK', 'EU', 'CM']
  const availableSizeTypes = Object.keys(universalSizes).sort((a, b) => {
    const indexA = sizeTypeOrder.indexOf(a)
    const indexB = sizeTypeOrder.indexOf(b)
    if (indexA === -1 && indexB === -1) return a.localeCompare(b)
    if (indexA === -1) return 1
    if (indexB === -1) return -1
    return indexA - indexB
  })

  useEffect(() => {
    const preferredSizeRange = localStorage.getItem('preferred_size_range') || 'EU'
    if (availableSizeTypes.includes(preferredSizeRange)) {
      setActiveSizeType(preferredSizeRange)
    } else if (availableSizeTypes.length > 0) {
      setActiveSizeType(availableSizeTypes[0])
    }
  }, [availableSizeTypes])

  const handleSizeTypeClick = (sizeType) => {
    setActiveSizeType(sizeType)
    localStorage.setItem('preferred_size_range', sizeType)
    window.dispatchEvent(new CustomEvent('preferred_size_range_changed', {
      detail: { sizeType }
    }))
  }

  return (
    <div className="flex flex-col gap-y-1">
      {nonUniversalSizes.length > 0 && (
        <div className="flex flex-wrap items-center gap-0.5 w-full">
          {nonUniversalSizes.map((item) => (
            <SizeGridItem key={item.value} item={item} onFilter={onFilter} />
          ))}
        </div>
      )}

      {availableSizeTypes.length > 0 && (
        <div className="flex flex-col gap-y-1">
          <div className="flex items-stretch bg-neutral-light rounded-80" role="tablist">
            {availableSizeTypes.map((sizeType) => (
              <button
                key={sizeType}
                role="tab"
                aria-selected={activeSizeType === sizeType}
                className="flex items-center justify-center w-full h-full min-h-[44px] py-0.5 px-0.75 border border-transparent rounded-80 cursor-pointer aria-selected:bg-brand-white aria-selected:border-brand-black hover:border-brand-black transition-all"
                onClick={() => handleSizeTypeClick(sizeType)}
              >
                <span className="text-b leading-none font-semibold uppercase pointer-events-none">
                  {sizeType === 'EU' ? 'EU' : sizeType}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-0.5 w-full">
            {universalSizes[activeSizeType]?.map((item) => (
              <SizeGridItem key={item.value} item={item} onFilter={onFilter} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SizeGridItem = ({ item, onFilter }) => {
  return (
    <div
      className={`
        btn btn-light btn-fill btn-m w-full max-w-grid-1/3 !text-s !font-normal !px-0.25 !rounded-none
        ${item.checked ? 'active' : ''}
      `}
      onClick={() => onFilter(item.value)}
    >
      <input
        type="checkbox"
        checked={item.checked}
        onChange={() => onFilter(item.value)}
        className="sr-only"
        aria-label={item.displayLabel || item.label}
      />
      <span>{item.size || item.label}</span>
    </div>
  )
}

const parseSizeValue = (sizeValue, sizeType) => {
  if (!sizeValue) return sizeValue

  if (!sizeValue.includes('-')) {
    return sizeValue
  }

  const regions = sizeValue.split('-').map(r => r.trim()).filter(r => r)

  for (const region of regions) {
    const cleanRegion = region.trim()

    if (sizeType === 'UK' && cleanRegion.startsWith('UK ')) {
      const ukContent = cleanRegion.replace('UK ', '').trim()
      if (ukContent.includes('/')) {
        return ukContent
      }
      return ukContent
    }

    if (sizeType === 'US' && cleanRegion.startsWith('US ')) {
      const usContent = cleanRegion.replace('US ', '').trim()
      if (usContent.includes('/')) {
        return usContent
      }
      return usContent
    }

    if (sizeType === 'EU' && cleanRegion.startsWith('EURO ')) {
      return cleanRegion.replace('EURO ', '').trim()
    }

    if (sizeType === 'CM' && cleanRegion.startsWith('CM ')) {
      return cleanRegion.replace('CM ', '').trim()
    }
  }

  return sizeValue
}

const parseSizeData = (values) => {
  const universalSizes = {}
  const nonUniversalSizes = []

  values.forEach((item) => {
    const isAvailable = !item.disabled && (item.count === undefined || item.count > 0)

    if (!isAvailable) return

    const sizingString = item.label

    if (sizingString?.includes('-')) {
      const regions = sizingString.split('-').map(r => r.trim()).filter(r => r)
      const sizeDataByType = {}

      regions.forEach(region => {
        const cleanRegion = region.trim()

        if (cleanRegion.startsWith('UK ')) {
          const ukContent = cleanRegion.replace('UK ', '').trim()
          sizeDataByType.UK = {
            size: ukContent,
            value: item.value,
            count: item.count,
            ...item
          }
        } else if (cleanRegion.startsWith('US ')) {
          const usContent = cleanRegion.replace('US ', '').trim()
          sizeDataByType.US = {
            size: usContent,
            value: item.value,
            count: item.count,
            ...item
          }
        } else if (cleanRegion.startsWith('EURO ')) {
          const euroContent = cleanRegion.replace('EURO ', '').trim()
          sizeDataByType.EU = {
            size: euroContent,
            value: item.value,
            count: item.count,
            ...item
          }
        } else if (cleanRegion.startsWith('CM ')) {
          const cmContent = cleanRegion.replace('CM ', '').trim()
          sizeDataByType.CM = {
            size: cmContent,
            value: item.value,
            count: item.count,
            ...item
          }
        }
      })

      Object.keys(sizeDataByType).forEach(sizeType => {
        if (!universalSizes[sizeType]) {
          universalSizes[sizeType] = []
        }

        const sizeData = sizeDataByType[sizeType]
        const parsedSize = parseSizeValue(sizingString, sizeType)

        universalSizes[sizeType].push({
          ...sizeData,
          size: parsedSize,
          sizeType,
          displayLabel: `${sizeType === 'EU' ? 'EU' : sizeType} ${parsedSize}`
        })
      })
    } else {
      nonUniversalSizes.push(item)
    }
  })

  Object.keys(universalSizes).forEach(sizeType => {
    universalSizes[sizeType].sort((a, b) => {
      const aNum = parseFloat(a.size.toString().match(/\d+\.?\d*/)?.[0] || 0)
      const bNum = parseFloat(b.size.toString().match(/\d+\.?\d*/)?.[0] || 0)
      return aNum - bNum
    })
  })

  return { universalSizes, nonUniversalSizes }
}
